// src/core/utils/adMeta.js
//
// Advertisement / marketing attribution helper (framework-agnostic, no Vue deps).
//
// TWO MECHANISMS KEEP ATTRIBUTION ATTACHED TO EXACTLY ONE ORDER:
//
//   1. URL STRIP ON CAPTURE. captureAdMeta() removes the tracking params from
//      the address bar via history.replaceState the moment it reads them, so a
//      reload / SPA navigation can NEVER re-capture the same click. (Leaving the
//      params in the URL was the cause of meta being re-attached to new orders.)
//
//   2. CONSUME ON USE. The caller (store: syncAdMetaToCart) clears the meta the
//      instant it is written to a real order. Once used, there is nothing left
//      in storage to attach to any future order - so after checkout, no new
//      order gets the same meta.
//
// LAST-TOUCH, SINGLE SERVICE: only ONE ad service is ever stored - the one from
// the most recent ad link. A MailChimp link after a UTM link REPLACES the UTM
// data. meta is { utm: {...}, capturedAt } OR { mc: {...}, capturedAt }.
//
// SCALABLE: add a service = one TRACKERS entry. `dataLayer` controls whether it
// is pushed to GTM/GA4 on the purchase event.

const STORAGE_KEY = "adMeta";

// -----------------------------------------------------------------------------
// THE ONLY THING YOU EDIT TO ADD A SERVICE.
//   key       - sub-object key under meta (meta.utm, meta.mc, ...)
//   prefix    - stripped from each param to form the stored field name
//   params    - full URL param names to read (and strip from the URL)
//   dataLayer - true => pushed as original_<param>; false => stored only
// If a URL carries more than one service, the FIRST in this list wins.
// -----------------------------------------------------------------------------
const TRACKERS = [
  {
    key: "utm",
    prefix: "utm_",
    params: [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
      "utm_id",
    ],
    dataLayer: true,
  },
  {
    key: "mc",
    prefix: "mc_",
    params: ["mc_cid", "mc_eid"],
    dataLayer: false,
  },
  // { key: "gg", prefix: "", params: ["gclid"],  dataLayer: false },
  // { key: "fb", prefix: "", params: ["fbclid"], dataLayer: false },
];

export function parseMaybeJSON(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
}

function serviceOf(meta) {
  if (!meta) return null;
  for (const t of TRACKERS) if (meta[t.key]) return t.key;
  return null;
}

// Read the FIRST tracked service present on the URL. Returns { key, data } | null.
function readServiceFromUrl(search = window.location.search) {
  const params = new URLSearchParams(search || "");
  for (const tracker of TRACKERS) {
    const data = {};
    let found = false;
    for (const param of tracker.params) {
      const val = params.get(param);
      if (val != null && val !== "") {
        data[param.slice(tracker.prefix.length)] = val;
        found = true;
      }
    }
    if (found) return { key: tracker.key, data };
  }
  return null;
}

// Remove every tracked param from the visible URL, preserving path, other query
// params and the hash. No navigation / no reload.
function stripTrackingParamsFromUrl() {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const tracker of TRACKERS) {
      for (const param of tracker.params) {
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param);
          changed = true;
        }
      }
    }
    if (changed) {
      const qs = url.searchParams.toString();
      const newUrl = url.pathname + (qs ? `?${qs}` : "") + url.hash;
      window.history.replaceState(window.history.state, "", newUrl);
    }
  } catch {
    // history / URL unavailable - non-fatal
  }
}

// Last-touch, single-service capture. Call once, early, in main.js.
// Stores the service (replacing any previous one) AND strips the params from the
// URL so it can't be captured again on reload.
export function captureAdMeta() {
  const incoming = readServiceFromUrl();
  if (!incoming) return getAdMeta(); // no tracked params on this URL

  // Strip immediately, whether or not the values are new, so reloads are inert.
  stripTrackingParamsFromUrl();

  const existing = getAdMeta();
  const sameAsStored =
    existing &&
    serviceOf(existing) === incoming.key &&
    JSON.stringify(existing[incoming.key]) === JSON.stringify(incoming.data);
  if (sameAsStored) return existing;

  const meta = {
    [incoming.key]: incoming.data,
    capturedAt: new Date().toISOString(),
  };
  saveAdMeta(meta);
  return meta;
}

export function getAdMeta() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Write meta back to storage. Used by the store to restore it if an attach
// attempt fails, so it can retry later.
export function saveAdMeta(meta) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(meta));
  } catch {
    // ignore
  }
}

export function clearAdMeta() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Flatten meta into GTM/GA4 purchase-event fields, ONLY for services flagged
// dataLayer:true. UTM meta -> original_utm_* ; MailChimp meta -> {} (stored only).
export function buildTrackingDataLayerFields(meta) {
  const parsed = parseMaybeJSON(meta);
  if (!parsed) return {};

  const out = {};
  for (const tracker of TRACKERS) {
    if (!tracker.dataLayer) continue;
    const group = parsed[tracker.key];
    if (!group) continue;
    for (const param of tracker.params) {
      const storedKey = param.slice(tracker.prefix.length);
      const val = group[storedKey];
      if (val != null && val !== "") {
        out[`original_${param}`] = val;
      }
    }
  }
  return out;
}